import { useEffect } from 'react';

/**
 * ChunkLoadError auto-retry handler for intelligence module
 *
 * When a dynamic import fails (e.g., network issues, CDN cache invalidation),
 * this handler will automatically reload the page to retry loading the chunk.
 * Uses sessionStorage to track retry attempts and prevent infinite reload loops.
 */
const CHUNK_RETRY_KEY = 'intelligence_chunk_load_retry';
const MAX_CHUNK_RETRIES = 3;

function handleChunkLoadError(error: Error): void {
  const isChunkLoadError =
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Failed to fetch dynamically imported module');

  if (!isChunkLoadError) {
    return;
  }

  let retryData: { count?: number; url?: string } = {};
  try {
    const raw = sessionStorage.getItem(CHUNK_RETRY_KEY);
    retryData = raw ? JSON.parse(raw) : {};
  } catch {
    sessionStorage.removeItem(CHUNK_RETRY_KEY);
  }
  const { count = 0, url = '' } = retryData;
  const currentUrl = window.location.href;

  // Reset retry count if URL changed
  const retryCount = url === currentUrl ? count : 0;

  if (retryCount < MAX_CHUNK_RETRIES) {
    sessionStorage.setItem(
      CHUNK_RETRY_KEY,
      JSON.stringify({ count: retryCount + 1, url: currentUrl }),
    );

    console.warn(
      `ChunkLoadError: Retry attempt ${retryCount + 1}/${MAX_CHUNK_RETRIES}`,
      error.message,
    );

    window.location.reload();
  } else {
    sessionStorage.removeItem(CHUNK_RETRY_KEY);
    console.error(`ChunkLoadError: Failed after ${MAX_CHUNK_RETRIES} retries`, error.message);
    // Error will be reported to Aegis via normal error flow
  }
}

/**
 * Custom hook to handle ChunkLoadError with auto-retry for intelligence pages
 *
 * @example
 * // In a layout or page component:
 * function IntelligenceLayout() {
 *   useChunkLoadErrorHandler();
 *   return <div>...</div>;
 * }
 */
export function useChunkLoadErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error) {
        handleChunkLoadError(event.error);
      }
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof Error) {
        handleChunkLoadError(event.reason);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
}

export default useChunkLoadErrorHandler;
